import React from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { useSelector } from "react-redux";
import t from "../../../utils/translation/translator";
import MsxIcons from "../../../components/Icons/MsxIcons/MsxIcons";
import "./Input.scss";
import {
  selectCurrentFiltersStepperStep,
  selectRetrofitCurrentStep,
  selectShowAxisPopup
} from "../../../../Retrofit/store/slices/retrofit";

const Input = ({
  label,
  unit,
  name,
  min,
  max,
  tooltip,
  tooltipId,
  isInvalid,
  invalidText,
  classes,
  inputClasses,
  labelClasses,
  recommended,
  placeHolder,
  labelRequired,
  unitClasses,
  minMaxTopLabelShow,
  onfocusin,
  onfocusout,
  mandatory,
  noDecimals,
  inputRounded = false,
  ...props
}) => {
  const showAxisPopup = useSelector(selectShowAxisPopup);
  const currentStep = useSelector(selectRetrofitCurrentStep);
  const currentFiltersStepperStep = useSelector(selectCurrentFiltersStepperStep);

  const getIdStep = () => {
    const answersKeySet = {
      2: "application",
      3: "specify",
      3.1: "select",
      3.2: "compare",
      5: "finalize"
    };

    const step = showAxisPopup ? `${currentStep?.id}.${currentFiltersStepperStep}` : currentStep?.id;

    return answersKeySet[step];
  };

  return (
    <div className={`Input flex-1 md:text-xs space-y-2 w-full md:w-auto ${classes}`}>
      <div className="flex items-center space-x-1 flex-wrap">
        {label && (
          <span
            className={`
         ${labelClasses}`}
          >
            {label}
            {labelRequired && <span className="light-error-active">*</span>}
          </span>
        )}
        {tooltip && (
          <div data-tip data-for={tooltipId} className="cursor-pointer flex" tabIndex={-1}>
            <MsxIcons name="info-tooltip" size="nano" />
            <ReactTooltip
              id={tooltipId}
              place="right"
              effect="solid"
              backgroundColor="#FFFFFF"
              textColor="rgb(31 41 55)"
              className="shadow-[0px_0px_8px_rgba(98,100,105,0.5)] rounded-[10px]"
            >
              <div>{tooltip}</div>
            </ReactTooltip>
          </div>
        )}
        {mandatory && <span className="text-xxs">*</span>}
        {/* if min or max are valid numbers */}
        {minMaxTopLabelShow && (min !== null || max !== null) && (
          <span className="md:text-xxs text-gray-400">
            ({(min && Math.round(min * 100) / 100)?.toString() || "..."} -{" "}
            {(max && Math.round(max * 100) / 100)?.toString() || "..."} {unit})
          </span>
        )}
        {recommended && <span className="text-xs text-gray-400">({t("msizer.ui.recommended")})</span>}
      </div>

      <div className="relative flex items-end space-x-1">
        <input
          id={`${getIdStep()}-input-${label.toLowerCase().replace(/[^A-Z0-9]/gi, "")}`}
          className={`flex-1 ${
            !inputRounded ? "rounded md:rounded-none" : ""
          } max-w-[85%] md:max-w-full border-[1px] border-gray-400 px-3 py-2 md:px-2 md:py-1 md:text-xs
         out-of-range:border-red-400 out-of-range:bg-red-100 out-of-range:focus:ring-red-400 focus:border-transparent focus:ring-secondary
         !outline-transparent
         ${isInvalid ? "ring-red-400 bg-red-100 focus:ring-red-400 mb-1" : ""}
         ${inputClasses}`}
          placeholder={placeHolder !== "" ? placeHolder : t("msizer.ui.specify.filter_input_placeholder")}
          step="any"
          name={name}
          min={min}
          max={max}
          onFocus={(event) => {
            event.target.select();
            if (onfocusin !== null && onfocusin !== undefined) onfocusin(event);
          }}
          onBlur={onfocusout}
          onKeyDown={(e) => {
            if (noDecimals && (e.key === "," || e.key === ".")) {
              e.preventDefault();
            }
          }}
          {...props}
        />
        {unit && <span className={`absolute right-2 select-none text-gray-400 font-light ${unitClasses}`}>{unit}</span>}
      </div>
      {isInvalid ? (
        <div className="text-red-600 flex items-center gap-1 !-mt-0.5">
          <MsxIcons name="alert" size="nano" />
          <div>{invalidText}</div>
        </div>
      ) : null}
    </div>
  );
};

Input.propTypes = {
  label: PropTypes.string,
  unit: PropTypes.string,
  name: PropTypes.string.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  tooltip: PropTypes.string,
  tooltipId: PropTypes.string,
  isInvalid: PropTypes.bool,
  classes: PropTypes.string,
  inputClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  invalidText: PropTypes.string,
  recommended: PropTypes.bool,
  placeHolder: PropTypes.string,
  labelRequired: PropTypes.bool,
  unitClasses: PropTypes.string,
  minMaxTopLabelShow: PropTypes.bool,
  onfocusin: PropTypes.func,
  onfocusout: PropTypes.func,
  mandatory: PropTypes.bool,
  noDecimals: PropTypes.bool
};

Input.defaultProps = {
  label: null,
  unit: null,
  tooltip: null,
  min: null,
  max: null,
  tooltipId: null,
  isInvalid: false,
  classes: "",
  inputClasses: "",
  labelClasses: "",
  invalidText: "",
  recommended: false,
  placeHolder: "",
  labelRequired: false,
  unitClasses: "",
  minMaxTopLabelShow: true,
  onfocusin: null,
  onfocusout: null,
  mandatory: false,
  noDecimals: false
};

export default Input;
